import { createContext, useEffect, useState } from 'react';

import { GridUrlParams } from '../types/types';
import { useParams } from 'react-router-dom';

import { useAidsListByOriginator, useArchivalAidsList } from './aids-context';

import { useArchivalUnitDetail } from '../hooks/archival-unit-detail-hook';
import { useEadDetail } from '../hooks/ead-detail-hook';
import { useArchivalCollections } from '../hooks/archival-collection-hook';
import { ArchivalAidMetadata, EadNodeParentView } from 'types';

export enum GridRootUrl {
  COLLECTIONS = '/fondy',
  AIDS = '/pomucky',
}

// export enum GridRootUrl {
//   COLLECTIONS = '/test-fondy',
//   AIDS = '/test-pomucky',
// }

type Hierarchy = {
  archivalCollectionId?: string;
  archivalAidId?: string;
  eadId?: string;
  archivalUnitId?: string;
  rootUrl?: GridRootUrl;
  getRootId?: () => string;
};

export const findValidDescribingAidId = (aids?: ArchivalAidMetadata[]) => {
  return (aids ?? []).find((a) => !a.invalidated)?.id;
};

export const findValidDescribingAid = (aids?: ArchivalAidMetadata[]) => {
  return (aids ?? []).find((a) => !a.invalidated);
};

export const HierarchyContext = createContext<Hierarchy>({});

export const useCollectionsHierarchy = () => {
  const [hierarchy, setHierarchy] = useState<Hierarchy>({
    rootUrl: GridRootUrl.COLLECTIONS,
  });

  const { id, archivalAidId, eadId, archivalUnitId } = useParams<
    GridUrlParams
  >();

  const searchParams = new URLSearchParams(window.location.search);
  const originatorId = searchParams.get('originator') ?? undefined;

  const response = useArchivalCollections(originatorId);

  const archivalUnitResponse = useArchivalUnitDetail(archivalUnitId);
  const eadResponse = useEadDetail(eadId);

  useEffect(() => {
    let archivalIadIdFromEad: string | undefined = undefined;
    if (eadResponse.data) {
      let par = (eadResponse.data as unknown) as EadNodeParentView;
      let fallback = 100;
      while (!par.archivalAidMetadata && fallback--) {
        par = par?.parent;
      }
      archivalIadIdFromEad = par.archivalAidMetadata?.id;
    }

    setHierarchy((p) => ({
      ...p,
      archivalCollectionId: id,
      archivalAidId:
        archivalAidId ??
        findValidDescribingAidId(
          archivalUnitResponse.data?.archivalUnitReference
            ?.describingArchivalAids ?? []
        ) ??
        archivalIadIdFromEad,
      eadId,
      archivalUnitId,
      getRootId: () => id,
    }));
  }, [
    id,
    archivalAidId,
    eadId,
    archivalUnitId,
    archivalUnitResponse.data?.archivalUnitReference?.describingArchivalAids,
    eadResponse.data,
  ]);

  if (response.isLoading) {
    return { loading: true };
  }

  // if (!id || (id === 'undefined' && response.data?.items?.[0]?.id)) {
  //   if (originatorId) {
  //     push(
  //       `${GridRootUrl.COLLECTIONS}/${response.data?.items?.[0]?.id}?originator=${originatorId}`
  //     );
  //   } else {
  //     push(`${GridRootUrl.COLLECTIONS}/${response.data?.items?.[0]?.id}`);
  //   }
  // }

  return {
    hierarchy,
    setHierarchy,
    loading: response.isLoading,
    collections: response.data ?? {},
  };
};

export const useAidsHierarchy = () => {
  const [hierarchy, setHierarchy] = useState<Hierarchy>({
    rootUrl: GridRootUrl.AIDS,
  });

  const searchParams = new URLSearchParams(window.location.search);
  const originatorId = searchParams.get('originator') ?? undefined;

  const { id, eadId, archivalUnitId } = useParams<GridUrlParams>();

  const response = useArchivalAidsList();
  const aidsByOriginator = useAidsListByOriginator(originatorId);

  useEffect(() => {
    setHierarchy((p) => ({
      ...p,
      archivalAidId: id,
      eadId,
      archivalUnitId,
      getRootId: () => id,
      rootUrl: GridRootUrl.AIDS,
    }));
  }, [id, eadId, archivalUnitId]);

  if (response.isLoading || aidsByOriginator.isLoading) {
    return { loading: true };
  }

  // TODO: vyriesit automaticke presmerovanie na prve ID - upustil by som od toho
  // if (!id || id === 'undefined') {
  //   if (originatorId) {
  //     const firstKey = Object.keys(aidsByOriginator.data ?? {})[0];
  //     push(
  //       `${GridRootUrl.AIDS}/${aidsByOriginator.data?.[firstKey]?.[0]?.id}?originator=${originatorId}`
  //     );
  //   } else {
  //     push(`${GridRootUrl.AIDS}/${response.data?.items?.[0]?.id}`);
  //   }
  // }

  return {
    hierarchy,
    setHierarchy,
    loading: response.isLoading,
    listByOriginatorResult: aidsByOriginator?.data ?? {},
    listResult: response.data ?? { items: [], count: 0 },
  };
};

/*
fondy:

fondy/:id
fondy/:id /pomucka/    :archivalAidId
fondy/:id /archivalie/ :archivalUnitId
fondy/:id /ead/        :eadId


pomucky:

pomucky/:id
pomucky/:archivalAidId/archivalie/:archivalUnitId
pomucky/:archivalAidId/ead/:eadId


*/
